import { supabase } from '../../config/supabase';
import { ApiError, RetryUtil, BackoffStrategy } from '../../errors';
import { Logger } from '../../utils/logger';

/**
 * Result of a plain table query. Mirrors the shape of a Supabase response but with a simple
 * error object, so callers keep their existing "check error, then use data" flow.
 */
export interface DbResult<T> {
  data: T | null;
  error: DbError | null;
}

/** Database error as returned by Supabase (message plus any extra fields such as code) */
export type DbError = {
  message: string;
  code?: string | number;
  status?: number;
  details?: string;
  hint?: string;
};

/**
 * Messages that indicate a network-level failure. A database error carrying
 * neither a numeric code nor a status is only worth retrying when its message
 * matches one of these; anything else may be a permanent failure.
 */
const NETWORK_ERROR_PATTERNS = [
  'failed to fetch',
  'network',
  'fetch failed',
  'timeout',
  'timed out',
  'econnreset',
  'socket hang up',
];

function looksLikeNetworkFailure(message: string): boolean {
  const normalized = message.toLowerCase();
  return NETWORK_ERROR_PATTERNS.some(pattern => normalized.includes(pattern));
}

/**
 * Base API class for Supabase operations with consistent error handling
 */
export class BaseApi {
  /**
   * Create a typed API error with proper context
   */
  protected static createApiError(
    message: string,
    code = 500,
    details?: Record<string, unknown>
  ): ApiError {
    return new ApiError(message, code, {
      isOperational: true,
      context: details,
    });
  }

  /**
   * Execute a Supabase query with retry capabilities
   */
  protected static async executeWithRetry<T>(
    operationName: string,
    fn: () => Promise<{ data: T; error: DbError | null }>
  ): Promise<T> {
    try {
      // Use the retry utility for automatic retries
      return await RetryUtil.retry(
        async () => {
          const { data, error } = await fn();

          if (error) {
            // Only a numeric HTTP code or status can classify an error as
            // transient; Supabase error codes are often strings (e.g.
            // 'PGRST116'). Without either, retry only network-like failures —
            // anything else may be permanent and no retry can fix it.
            const numericCode = typeof error.code === 'number' ? error.code : undefined;
            const numericStatus = typeof error.status === 'number' ? error.status : undefined;
            const statusCode = numericCode ?? numericStatus ?? 500;
            const hasStringCode = typeof error.code === 'string' && error.code !== '';
            const isTransient =
              numericCode !== undefined || numericStatus !== undefined
                ? [429, 500, 502, 503, 504].includes(statusCode)
                : hasStringCode
                  ? false
                  : looksLikeNetworkFailure(error.message || '');

            throw new ApiError(error.message || `${operationName} failed`, statusCode, {
              isOperational: isTransient,
              context: {
                details: error.details,
                hint: error.hint,
                operation: operationName,
                retryable: isTransient,
              },
            });
          }

          if (data === null) {
            throw new ApiError(`${operationName} returned no data`, 404, {
              isOperational: true,
              context: { operation: operationName },
            });
          }

          return data;
        },
        {
          maxRetries: 3,
          delay: 1000,
          backoffStrategy: BackoffStrategy.EXPONENTIAL,
          shouldRetry: (error): boolean => {
            // Errors thrown above declare their retry decision in the context;
            // anything else (e.g. an exception raised by fn itself) keeps the
            // default transient-error heuristics.
            const retryable = error instanceof ApiError ? error.context?.retryable : undefined;
            return typeof retryable === 'boolean' ? retryable : RetryUtil.isTransientError(error);
          },
        }
      );
    } catch (error) {
      // If it's already an ApiError, just rethrow it
      if (error instanceof ApiError) {
        throw error;
      }

      // Otherwise wrap it in an ApiError
      throw new ApiError(error instanceof Error ? error.message : 'Unknown error', 500, {
        isOperational: false,
        cause: error instanceof Error ? error : undefined,
        context: { operation: operationName },
      });
    }
  }

  /**
   * Get the Supabase client
   */
  protected static getClient() {
    return supabase;
  }

  /**
   * Handle a response with a single item
   * @param response Response from Supabase query
   * @returns The item or throws an error
   */
  protected static async handleResponse<T>(response: {
    data: T | null;
    error: DbError | null;
  }): Promise<T> {
    if (response.error) {
      throw new ApiError(response.error.message, response.error.code as number, {
        context: { ...response.error },
      });
    }
    if (!response.data) {
      throw new ApiError('No data returned from the database');
    }
    return response.data;
  }

  /**
   * Handle a response with a list of items
   * @param response Response from Supabase query
   * @returns Array of items (empty if none found)
   */
  protected static async handleListResponse<T>(response: {
    data: T[] | null;
    error: DbError | null;
  }): Promise<T[]> {
    if (response.error) {
      throw new ApiError(response.error.message, response.error.code as number, {
        context: { ...response.error },
      });
    }
    return response.data || [];
  }

  /**
   * Handle a response from an operation that doesn't return data
   * @param response Response from Supabase operation
   */
  protected static async handleEmptyResponse(response: { error: DbError | null }): Promise<void> {
    if (response.error) {
      throw new ApiError(response.error.message, response.error.code as number, {
        context: { ...response.error },
      });
    }
  }

  /**
   * Handle a count response
   * @param response Response from Supabase count query
   * @returns The count value or 0
   * @throws ApiError when the count query failed (a real count of 0 still returns 0)
   */
  protected static handleCountResponse(response: {
    count: number | null;
    error: DbError | null;
  }): number {
    if (response.error) {
      Logger.serviceError('BaseApi', 'handleCountResponse', String(response.error), {
        context: 'BaseApi',
        operation: 'handleCountResponse',
      });
      const status =
        typeof response.error.code === 'number'
          ? response.error.code
          : typeof response.error.status === 'number'
            ? response.error.status
            : 500;
      throw this.createApiError(response.error.message || 'Count query failed', status, {
        operation: 'handleCountResponse',
      });
    }
    return response.count || 0;
  }
}
